import React from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

export function LobbyScreen({ send }: Props) {
  const { 
    players, 
    isHost,
    settings,
    setPhase,
  } = useGameStore();

  const handleStartGame = () => {
    send('startGame');
  };

  const handleBackToSettings = () => {
    setPhase('settings');
  };

  return (
    <div className="screen">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h1 className="title-pixel" style={{ fontSize: 24, marginBottom: 8 }}>
          🍞 Game Lobby
        </h1>
        <p className="subtitle">Waiting for players to join...</p>
        <p style={{ 
          color: 'var(--ltc-text-muted)', 
          fontSize: 12,
          marginTop: 8,
        }}>
          Your game is visible in the Browse menu
        </p>
      </div>

      {/* Game Settings Summary */}
      <div style={{ 
        display: 'flex', 
        gap: 16, 
        marginBottom: 24,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        <span style={{ 
          background: 'var(--ltc-bg-secondary)',
          padding: '8px 16px',
          border: 'var(--ltc-border-light)',
          fontSize: 14,
        }}>
          ⏱️ {settings.dayDuration} min days
        </span>
        <span style={{ 
          background: 'var(--ltc-bg-secondary)',
          padding: '8px 16px',
          border: 'var(--ltc-border-light)',
          fontSize: 14,
          textTransform: 'capitalize',
        }}>
          🎯 {settings.difficulty}
        </span>
        <span style={{ 
          background: 'var(--ltc-bg-secondary)',
          padding: '8px 16px',
          border: 'var(--ltc-border-light)',
          fontSize: 14,
        }}>
          👥 Max {settings.maxPlayers}
        </span>
      </div>

      {/* Player List */}
      <div className="card" style={{ width: '100%', maxWidth: 400 }}>
        <div className="card-header">
          <h2 style={{ 
            fontSize: 18, 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span>Players</span>
            <span style={{ 
              fontSize: 14,
              color: 'var(--ltc-text-muted)',
            }}>
              {players.length}/{settings.maxPlayers}
            </span>
          </h2>
        </div>

        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {players.map((player, index) => (
            <div key={player.playerId} className="player-item">
              <div 
                className="player-avatar pixel-img"
                style={{ 
                  background: `hsl(${index * 60}, 60%, 70%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                }}
              >
                {['🧑‍🍳', '👩‍🍳', '🧑', '👩', '👨', '🧓'][index % 6]}
              </div>
              <span className="player-name">{player.playerName}</span>
              {player.isHost && (
                <span className="player-badge">Host</span>
              )}
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: settings.maxPlayers - players.length }).map((_, i) => (
            <div 
              key={`empty-${i}`} 
              className="player-item"
              style={{ opacity: 0.4 }}
            >
              <div 
                className="player-avatar"
                style={{ 
                  background: 'var(--ltc-bg-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ?
              </div>
              <span className="player-name" style={{ fontStyle: 'italic' }}>
                Waiting...
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ 
        marginTop: 32, 
        display: 'flex', 
        gap: 16,
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {isHost ? (
          <>
            <button
              className="btn btn-accent btn-large"
              onClick={handleStartGame}
              disabled={players.length < 2}
              style={{ minWidth: 200 }}
            >
              🎮 Start Game
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleBackToSettings}
            >
              ⚙️ Edit Settings
            </button>
            {players.length < 2 && (
              <p style={{ 
                color: 'var(--ltc-text-muted)',
                fontSize: 14,
              }}>
                Need at least 2 players to start
              </p>
            )}
          </>
        ) : (
          <p style={{ 
            color: 'var(--ltc-text-secondary)',
            fontSize: 16,
            textAlign: 'center',
          }}>
            Waiting for host to start the game...
          </p>
        )}
      </div>
    </div>
  );
}
